import { supabase } from '../supabase';
import type { SenderIdentity } from '../messagesCore';

// Only a real Twilio CallSid identifies a call. The Voice SDK's temporary
// connection id (TJS…) never matches the row the server writes, so writing it
// would leave a second, orphaned call in the thread.
const isTwilioCallSid = (sid: string) => /^CA[0-9a-f]{32}$/i.test(sid);

/**
 * Ensure an outbound call appears in Messages by writing call_history from the
 * client. Edge functions (voice-handler / status webhook) also write rows; this
 * is a best-effort upsert so the thread still updates if those paths miss.
 */
export async function ensureOutboundCallHistory(params: {
  businessId: string;
  callSid: string;
  toNumber: string;
  fromNumber?: string | null;
  customerId?: string | null;
  status?: string;
  sender?: SenderIdentity | null;
}): Promise<void> {
  const {
    businessId,
    callSid,
    toNumber,
    fromNumber,
    customerId,
    status = 'ringing',
    sender,
  } = params;

  if (!supabase || !businessId || !callSid || !toNumber) return;
  if (!isTwilioCallSid(callSid)) return;
  const senderCols = sender
    ? { sender_kind: sender.kind, sender_id: sender.id, sender_name: sender.name }
    : {};

  try {
    let resolvedCustomerId = customerId || null;
    let resolvedFrom = fromNumber || null;

    if (!resolvedFrom) {
      const { data: biz } = await supabase
        .from('businesses')
        .select('VITE_TWILIO_PHONE_NUMBER')
        .eq('id', businessId)
        .maybeSingle();
      resolvedFrom = biz?.VITE_TWILIO_PHONE_NUMBER || null;
    }

    if (!resolvedCustomerId) {
      const digits = toNumber.replace(/\D/g, '');
      const last10 = digits.slice(-10);
      if (last10.length === 10) {
        const { data: customers } = await supabase
          .from('customers')
          .select('id, phone')
          .eq('business_id', businessId)
          .not('phone', 'is', null);

        const match = (customers || []).find((c) => {
          if (!c.phone) return false;
          return c.phone.replace(/\D/g, '').slice(-10) === last10;
        });
        resolvedCustomerId = match?.id || null;
      }
    }

    const row = {
      business_id: businessId,
      call_sid: callSid,
      from_number: resolvedFrom,
      to_number: toNumber,
      direction: 'outbound' as const,
      status,
      call_time: new Date().toISOString(),
      viewed: true,
      customer_id: resolvedCustomerId,
      ...senderCols,
    };

    const { data: existing } = await supabase
      .from('call_history')
      .select('id')
      .eq('call_sid', callSid)
      .maybeSingle();

    if (existing?.id) {
      await supabase
        .from('call_history')
        .update({
          status,
          customer_id: resolvedCustomerId || undefined,
          from_number: resolvedFrom || undefined,
          to_number: toNumber,
          ...senderCols,
        })
        .eq('id', existing.id);
    } else {
      const { error } = await supabase.from('call_history').insert(row);
      if (error) {
        console.error('[Call History] Failed to insert outbound call:', error);
      }
    }
  } catch (error) {
    console.error('[Call History] Error ensuring outbound call row:', error);
  }
}
